/**
 * Tickets — list view with filters, sortable columns, bulk select, and
 * inline bulk actions. Mobile-first: cards on phones, table on desktop.
 * Route: /tickets
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, TextField, MenuItem, Button, IconButton,
  LinearProgress, Tooltip, Stack, InputAdornment, useMediaQuery, useTheme, Collapse,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import axios from 'axios';
import { useTicketDialog, PriorityChip } from '../components/TicketDialog';
import ResponsiveTable from '../components/ResponsiveTable';
import PageSearchBar from '../components/PageSearchBar';
import { format } from 'date-fns';

const api = axios.create({ baseURL: '' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const STATUS_COLORS = {
  'New': 'info',
  'Open': 'primary',
  'In Progress': 'warning',
  'Waiting': 'default',
  'Resolved': 'success',
  'Closed': 'default',
};

const DUE_PRESETS = [
  { value: '',          label: 'Any' },
  { value: 'overdue',   label: 'Overdue' },
  { value: 'today',     label: 'Due today' },
  { value: 'this_week', label: 'This week' },
  { value: 'next_7',    label: 'Next 7 days' },
  { value: 'next_30',   label: 'Next 30 days' },
  { value: 'none',      label: 'No due date' },
];

export default function Tickets() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { openTicketDialog } = useTicketDialog();

  // Deep-link support: /tickets?open=<id> auto-opens the edit dialog. Used by
  // the global search bar in Layout.js (Tom Clancy / Mike Murray, 2026-04-24).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (openId) {
      openTicketDialog({ ticket_id: parseInt(openId, 10) });
      // Clean the URL so subsequent navigation doesn't keep re-opening it.
      const url = new URL(window.location.href);
      url.searchParams.delete('open');
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta]       = useState({
    statuses: [], priorities: [], ticket_types: [],
    ticket_type_options: [], ticket_type_groups: [],
    departments: [],
  });
  const [users, setUsers]     = useState([]);
  const [filters, setFilters] = useState({
    search: '', status: '', priority: '', department: '', ticket_type: '', mine: '', due: '',
  });
  const [sort, setSort]       = useState({ sortBy: '', sortDir: 'asc' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [stats, setStats]       = useState({});
  const [showFilters, setShowFilters] = useState(false); // mobile: collapse filters by default
  const [toast, setToast]       = useState({ open: false, severity: 'success', message: '' });

  // Bulk-action dialog state
  const [bulkDialog, setBulkDialog] = useState({ open: false, kind: null, value: '' });

  useEffect(() => {
    api.get('/api/tickets/meta').then(r => setMeta(r.data)).catch(() => {});
    // Users list for "Assign to" — lightweight reuse of messaging route (auth-protected).
    api.get('/api/messaging/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    const params = {};
    for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
    if (sort.sortBy) { params.sort_by = sort.sortBy; params.sort_dir = sort.sortDir; }
    api.get('/api/tickets', { params })
      .then(r => setRows(r.data.tickets || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    api.get('/api/tickets/stats/summary').then(r => setStats(r.data || {})).catch(() => {});
  }, [filters, sort]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  useEffect(() => {
    const handler = () => fetchTickets();
    window.addEventListener('ticket:updated', handler);
    return () => window.removeEventListener('ticket:updated', handler);
  }, [fetchTickets]);

  // Drop selections that leave the visible set after a reload.
  useEffect(() => {
    if (!selectedIds.length) return;
    const visible = new Set(rows.map(r => r.id));
    const next = selectedIds.filter(id => visible.has(id));
    if (next.length !== selectedIds.length) setSelectedIds(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const clearFilters = () => {
    setFilters({ search: '', status: '', priority: '', department: '', ticket_type: '', mine: '', due: '' });
    setSearchInput('');
  };

  const handleSearchSubmit = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      setFilters(f => ({ ...f, search: searchInput }));
    }
  };

  const set = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));

  const fmt = (d) => d ? format(new Date(d), 'MMM d, h:mm a') : '—';

  // ── Bulk actions ────────────────────────────────────────────────────────
  const openBulk = (kind) => setBulkDialog({ open: true, kind, value: '' });
  const closeBulk = () => setBulkDialog({ open: false, kind: null, value: '' });

  const submitBulk = async () => {
    if (!selectedIds.length) return;
    const { kind, value } = bulkDialog;
    let updates = null;
    if (kind === 'close')       updates = { status: 'Closed' };
    else if (kind === 'resolve') updates = { status: 'Resolved' };
    else if (kind === 'status') {
      if (!value) return;
      updates = { status: value };
    } else if (kind === 'assign') {
      updates = { assigned_to: value === '__unassign__' ? null : parseInt(value, 10) };
    } else if (kind === 'agent') {
      updates = { agent_id: value === '__unassign__' ? null : parseInt(value, 10) };
    } else if (kind === 'priority') {
      if (!value) return;
      updates = { priority: value };
    } else if (kind === 'department') {
      if (!value) return;
      updates = { department: value };
    }
    if (!updates) { closeBulk(); return; }

    try {
      const resp = await api.post('/api/tickets/bulk', { ids: selectedIds, updates });
      const { updated_count = 0, failed = [] } = resp.data || {};
      setToast({
        open: true,
        severity: failed.length ? 'warning' : 'success',
        message: failed.length
          ? `Updated ${updated_count}, ${failed.length} failed.`
          : `Updated ${updated_count} ticket${updated_count === 1 ? '' : 's'}.`,
      });
      setSelectedIds([]);
      closeBulk();
      fetchTickets();
      window.dispatchEvent(new Event('ticket:updated'));
    } catch (err) {
      setToast({ open: true, severity: 'error', message: err?.response?.data?.error || 'Bulk update failed' });
    }
  };

  const bulkCount = selectedIds.length;

  // Columns for ResponsiveTable — all data-relevant ones are sortable.
  const columns = useMemo(() => ([
    {
      key: 'ticket_number',
      label: 'Ticket #',
      secondary: true,
      sortable: true,
      render: (t) => t.ticket_number,
    },
    {
      key: 'title',
      label: 'Title',
      primary: true,
      sortable: true,
      render: (t) => (
        <Box>
          <span>{t.title}</span>
          {t.description && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 400 }}>
              {t.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      mobileOrder: 1,
      sortable: true,
      render: (t) => <Chip size="small" label={t.status} color={STATUS_COLORS[t.status] || 'default'} />,
    },
    {
      key: 'priority',
      label: 'Priority',
      mobileOrder: 2,
      sortable: true,
      render: (t) => <PriorityChip value={t.priority} />,
    },
    {
      key: 'department',
      label: 'Dept',
      mobileOrder: 3,
      sortable: true,
      render: (t) => (t.department ? (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column' }}>
          <Chip size="small" label={t.department} variant="outlined" />
          {t.subtype && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {t.subtype}
            </Typography>
          )}
        </Box>
      ) : '—'),
    },
    {
      key: 'project',
      label: 'Client / Project',
      hideOnMobile: true,
      sortable: true,
      render: (t) => t.project_name || t.client_name || '—',
    },
    {
      key: 'job_number',
      label: 'Job',
      mobileOrder: 4,
      sortable: true,
      render: (t) => t.job_number ? (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{t.job_number}</Typography>
      ) : null,
    },
    {
      key: 'ar_code',
      label: 'AR',
      mobileOrder: 5,
      sortable: true,
      render: (t) => t.ar_code ? (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{t.ar_code}</Typography>
      ) : null,
    },
    {
      key: 'assigned_to_name',
      label: 'Assigned',
      hideOnMobile: true,
      sortable: true,
      render: (t) => t.assigned_to_name || <span style={{ color: '#999' }}>—</span>,
    },
    {
      key: 'agent_name',
      label: 'Agent',
      hideOnMobile: true,
      sortable: true,
      render: (t) => t.agent_name || <span style={{ color: '#999' }}>—</span>,
    },
    {
      key: 'due_at',
      label: 'Due',
      hideOnMobile: true,
      sortable: true,
      render: (t) => {
        if (!t.due_at) return <span style={{ color: '#999' }}>—</span>;
        const d = new Date(t.due_at);
        const overdue = d < new Date() && !['Resolved','Closed'].includes(t.status);
        return (
          <Typography variant="caption" sx={{ color: overdue ? 'error.main' : 'text.primary', fontWeight: overdue ? 600 : 400 }}>
            {fmt(t.due_at)}
          </Typography>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      hideOnMobile: true,
      sortable: true,
      render: (t) => <Typography variant="caption">{fmt(t.created_at)}</Typography>,
    },
  ]), []);

  // Bulk-action dialog content varies by kind.
  const bulkDialogTitle = {
    close:      `Close ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}?`,
    resolve:    `Mark ${bulkCount} ticket${bulkCount === 1 ? '' : 's'} resolved?`,
    status:     `Change status on ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}`,
    assign:     `Assign ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}`,
    agent:      `Set agent on ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}`,
    priority:   `Change priority on ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}`,
    department: `Change department on ${bulkCount} ticket${bulkCount === 1 ? '' : 's'}`,
  }[bulkDialog.kind] || 'Bulk update';

  const renderBulkBody = () => {
    const k = bulkDialog.kind;
    if (k === 'close' || k === 'resolve') {
      return (
        <Typography variant="body2">
          This will set status to <b>{k === 'close' ? 'Closed' : 'Resolved'}</b> on {bulkCount} ticket{bulkCount === 1 ? '' : 's'}.
        </Typography>
      );
    }
    if (k === 'status') {
      return (
        <TextField select fullWidth size="small" label="New status"
          value={bulkDialog.value} onChange={(e) => setBulkDialog(v => ({ ...v, value: e.target.value }))}>
          {meta.statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      );
    }
    if (k === 'priority') {
      return (
        <TextField select fullWidth size="small" label="New priority"
          value={bulkDialog.value} onChange={(e) => setBulkDialog(v => ({ ...v, value: e.target.value }))}>
          {meta.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </TextField>
      );
    }
    if (k === 'department') {
      return (
        <TextField select fullWidth size="small" label="New department"
          value={bulkDialog.value} onChange={(e) => setBulkDialog(v => ({ ...v, value: e.target.value }))}>
          {meta.departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
        </TextField>
      );
    }
    if (k === 'assign' || k === 'agent') {
      const labelText = k === 'assign' ? 'Assign to (Owner)' : 'Set agent (working this ticket)';
      return (
        <TextField select fullWidth size="small" label={labelText}
          value={bulkDialog.value} onChange={(e) => setBulkDialog(v => ({ ...v, value: e.target.value }))}>
          <MenuItem value="__unassign__"><em>{k === 'assign' ? 'Unassign' : 'Clear agent'}</em></MenuItem>
          {(users || []).map(u => (
            <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
          ))}
        </TextField>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 800, flex: 1 }}>
            🎫 Tickets
          </Typography>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => openTicketDialog()}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? <AddIcon fontSize="small" /> : 'New Ticket'}
          </Button>
        </Box>
        {!isMobile && (
          <Typography variant="caption" color="text.secondary">
            Single source for support, service, warranty, and internal issues — linked to projects, jobs, and AR codes.
          </Typography>
        )}

        {/* Stats chips (compact on mobile) */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip label={`${stats.open_total ?? 0} Open`}    color="primary" size="small" />
          <Chip label={`${stats.new_count ?? 0} New`}       color="info"    size="small" />
          <Chip label={`${stats.urgent_open ?? 0} Urgent`}  color="error"   size="small" />
          <Chip label={`${stats.mine_open ?? 0} Mine`}      color="warning" size="small" variant="outlined" />
        </Stack>
      </Box>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          useFlexGap
          flexWrap="wrap"
        >
          <PageSearchBar
            placeholder="Search title, client, AR, job…"
            value={searchInput}
            onChange={(v) => setSearchInput(v)}
            onKeyDown={handleSearchSubmit}
          />

          {isMobile ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(v => !v)}
              fullWidth
            >
              {showFilters ? 'Hide filters' : 'Filters'}
            </Button>
          ) : null}

          <Collapse in={!isMobile || showFilters} sx={{ width: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <TextField select size="small" label="Status" value={filters.status} onChange={set('status')} sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                <MenuItem value="">All</MenuItem>
                {meta.statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Priority" value={filters.priority} onChange={set('priority')} sx={{ minWidth: { xs: '100%', sm: 110 } }}>
                <MenuItem value="">All</MenuItem>
                {meta.priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Department" value={filters.department} onChange={set('department')} sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                <MenuItem value="">All</MenuItem>
                {meta.departments.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              {/* Ticket type — searchable, grouped by Zoho category. */}
              <Autocomplete
                size="small"
                options={meta.ticket_type_options || []}
                value={(meta.ticket_type_options || []).find(o => o.value === filters.ticket_type) || null}
                onChange={(_, v) => setFilters(f => ({ ...f, ticket_type: v?.value || '' }))}
                getOptionLabel={(opt) => opt?.label || ''}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                groupBy={(opt) => opt?.category || ''}
                autoHighlight
                clearOnBlur={false}
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
                renderInput={(params) => (
                  <TextField {...params} label="Type" placeholder="All — type to filter…" />
                )}
              />
              <TextField select size="small" label="Due" value={filters.due} onChange={set('due')} sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                {DUE_PRESETS.map(d => <MenuItem key={d.value || 'any'} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Scope" value={filters.mine} onChange={set('mine')} sx={{ minWidth: { xs: '100%', sm: 110 } }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Mine</MenuItem>
              </TextField>

              <Box sx={{ flex: { sm: 1 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <Tooltip title="Clear filters">
                  <IconButton onClick={clearFilters} size="small"><FilterAltOffIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton onClick={fetchTickets} size="small"><RefreshIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {/* Bulk action bar — appears when rows are selected */}
      <Collapse in={bulkCount > 0}>
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            mb: 2,
            borderColor: 'primary.main',
            bgcolor: 'primary.50',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Chip label={`${bulkCount} selected`} color="primary" size="small" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<CheckCircleOutlineIcon />} onClick={() => openBulk('resolve')}>Resolve</Button>
          <Button size="small" variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => openBulk('close')}>Close</Button>
          <Button size="small" variant="outlined" startIcon={<PersonAddAlt1Icon />} onClick={() => openBulk('assign')}>Assign</Button>
          <Button size="small" variant="outlined" startIcon={<EngineeringOutlinedIcon />} onClick={() => openBulk('agent')}>Agent</Button>
          <Button size="small" variant="outlined" startIcon={<FlagOutlinedIcon />} onClick={() => openBulk('priority')}>Priority</Button>
          <Button size="small" variant="outlined" startIcon={<CategoryOutlinedIcon />} onClick={() => openBulk('department')}>Department</Button>
          <Button size="small" variant="outlined" onClick={() => openBulk('status')}>Status…</Button>
          <Button size="small" onClick={() => setSelectedIds([])}>Clear</Button>
        </Paper>
      </Collapse>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <ResponsiveTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        onRowClick={(t) => openTicketDialog({ ticket_id: t.id })}
        emptyMessage="No tickets match your filters."
        sortBy={sort.sortBy}
        sortDir={sort.sortDir}
        onSortChange={setSort}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {/* Bulk confirmation dialog */}
      <Dialog open={bulkDialog.open} onClose={closeBulk} maxWidth="xs" fullWidth>
        <DialogTitle>{bulkDialogTitle}</DialogTitle>
        <DialogContent>{renderBulkBody()}</DialogContent>
        <DialogActions>
          <Button onClick={closeBulk}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitBulk}
            disabled={
              (bulkDialog.kind === 'status'     && !bulkDialog.value) ||
              (bulkDialog.kind === 'priority'   && !bulkDialog.value) ||
              (bulkDialog.kind === 'department' && !bulkDialog.value) ||
              (bulkDialog.kind === 'assign'     && !bulkDialog.value) ||
              (bulkDialog.kind === 'agent'      && !bulkDialog.value)
            }
          >Apply</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
